import {useEffect, useState} from 'react';
import {useParams, useNavigate, useSearchParams} from 'react-router-dom';
import {MapPin, Plus, Loader2, Search, Pencil, ChevronLeft, ChevronRight} from 'lucide-react';
import {trpc, RouterOutput, createMutationErrorHandler} from '@common-web/lib/trpc';
import {toast} from '@common-web/hooks/use-toast';
import {useHasPermission} from '@common-web/hooks/use-permissions';
import {CODE_PERMISSIONS} from '@common/permissions';
import {DetailHeader} from '@internal/components/DetailHeader';
import {Button} from '@common-web/components/ui/button';
import {Badge} from '@common-web/components/ui/badge';
import {Input} from '@common-web/components/ui/input';
import {Label} from '@common-web/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@common-web/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {accessCodeEntryMode, groupRequiresAccessCode} from '@common/enrollmentSite/types';

type LocationRow = RouterOutput['internal']['enrollmentSite']['listLocations']['rows'][number];

const PAGE_SIZE_OPTIONS = [15, 25, 50, 100];

export function EnrollmentLocationsPage() {
  const {groupSlug = ''} = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const canManage = useHasPermission(CODE_PERMISSIONS.enrollment.manage.key);
  const utils = trpc.useUtils();

  // Back target depends on entry point: the sites list (?from=sites) vs the builder.
  const fromSites = searchParams.get('from') === 'sites';

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [editing, setEditing] = useState<LocationRow | 'new' | null>(null);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const {data: site} = trpc.internal.enrollmentSite.get.useQuery({groupSlug});
  const {data, isLoading, isFetching} = trpc.internal.enrollmentSite.listLocations.useQuery({
    groupSlug,
    search,
    page,
    pageSize,
  });

  // Undefined until the site loads; assume gated so we don't flash the wrong empty state.
  const requiresCode = site ? groupRequiresAccessCode(site.siteConfig) : true;
  const dropdownMode = !!site && accessCodeEntryMode(site.siteConfig) === 'dropdown';
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const setActive = trpc.internal.enrollmentSite.setLocationActive.useMutation({
    onSuccess: () => utils.internal.enrollmentSite.listLocations.invalidate(),
    onError: createMutationErrorHandler('Could not update access code'),
  });

  return (
    <div className='space-y-4 px-6 py-6'>
      <DetailHeader
        backLabel={fromSites ? 'Back to sites' : 'Back to builder'}
        onBack={() => navigate(fromSites ? '/enrollment-sites' : `/enrollment-sites/${groupSlug}`)}
        icon={<MapPin className='h-5 w-5' />}
        title='Access codes'
        subtitle={
          site ? (
            <>
              Access codes for{' '}
              <span className='font-medium text-foreground'>{site.siteConfig.name}</span>
            </>
          ) : undefined
        }
        actions={
          canManage && requiresCode ? (
            <Button onClick={() => setEditing('new')}>
              <Plus className='mr-1.5 h-4 w-4' />
              Add access code
            </Button>
          ) : undefined
        }
      />

      {!requiresCode ? (
        <div className='rounded-lg border border-dashed bg-white p-10 text-center text-sm text-muted-foreground'>
          This site is public — members enter without an access code, so there are none to manage.
          Turn on “require an access code” in the builder to add them here.
        </div>
      ) : (
        <>
          {dropdownMode && (
            <div className='rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900'>
              This site uses the location dropdown. Members see each active location as “City,
              State”, or its label when those are blank.
            </div>
          )}
          <div className='flex items-center justify-between gap-3'>
            <div className='relative w-full max-w-sm'>
              <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder='Search access codes...'
                className='pl-9'
              />
            </div>
            <span className='shrink-0 text-sm text-muted-foreground'>
              {total.toLocaleString()} access code{total === 1 ? '' : 's'}
            </span>
          </div>

          <div className='overflow-hidden rounded-lg border bg-white'>
            <div className='overflow-x-auto'>
              <table className='w-full min-w-[720px] table-fixed text-sm'>
                <thead className='border-b bg-muted/30 text-left text-xs uppercase tracking-wide text-muted-foreground'>
                  <tr>
                    <th className='min-w-[200px] px-6 py-3 font-semibold'>Label</th>
                    <th className='w-[120px] px-6 py-3 font-semibold'>Access code</th>
                    <th className='w-[120px] px-6 py-3 font-semibold'>Agent ID</th>
                    <th className='w-[150px] px-6 py-3 font-semibold'>City / State</th>
                    <th className='w-[104px] px-6 py-3 font-semibold'>Status</th>
                    <th className='w-[150px] px-6 py-3' />
                  </tr>
                </thead>
                <tbody className={isFetching ? 'opacity-60 transition-opacity' : undefined}>
                  {isLoading ? (
                    <tr>
                      <td colSpan={6} className='py-16 text-center'>
                        <Loader2 className='mx-auto h-6 w-6 animate-spin text-muted-foreground' />
                      </td>
                    </tr>
                  ) : !data?.rows.length ? (
                    <tr>
                      <td colSpan={6} className='py-16 text-center text-sm text-muted-foreground'>
                        {search ? `No access codes match “${search}”.` : 'No access codes yet.'}
                      </td>
                    </tr>
                  ) : (
                    data.rows.map((loc) => (
                      <tr
                        key={loc.id}
                        className={'border-b last:border-0 ' + (loc.active ? '' : 'opacity-60')}
                      >
                        <td className='px-6 py-3.5'>
                          <div className='truncate font-medium'>{loc.label}</div>
                          {loc.parentAgentId && (
                            <div className='truncate text-xs text-muted-foreground'>
                              parent {loc.parentAgentId}
                            </div>
                          )}
                        </td>
                        <td className='px-6 py-3.5 font-mono text-xs'>{loc.accessCode}</td>
                        <td className='px-6 py-3.5 font-mono text-xs'>{loc.agentId}</td>
                        <td className='px-6 py-3.5 text-muted-foreground'>
                          {[loc.city, loc.state].filter(Boolean).join(', ') || '—'}
                        </td>
                        <td className='px-6 py-3.5'>
                          <Badge variant={loc.active ? 'success' : 'outline'}>
                            {loc.active ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td className='whitespace-nowrap px-6 py-3.5 text-right'>
                          {canManage && (
                            <div className='flex justify-end gap-1'>
                              <Button
                                variant='ghost'
                                size='sm'
                                className='h-8'
                                onClick={() => setEditing(loc)}
                              >
                                <Pencil className='h-3.5 w-3.5' />
                              </Button>
                              <Button
                                variant='ghost'
                                size='sm'
                                className='h-8 text-xs'
                                disabled={setActive.isPending}
                                onClick={() => setActive.mutate({id: loc.id, active: !loc.active})}
                              >
                                {loc.active ? 'Deactivate' : 'Reactivate'}
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className='flex items-center justify-between border-t px-6 py-3 text-sm text-muted-foreground'>
              <span>
                {total === 0
                  ? 'No results'
                  : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)} of ${total.toLocaleString()}`}
              </span>
              <div className='flex items-center gap-3'>
                <div className='flex items-center gap-2'>
                  <span>Rows per page</span>
                  <Select
                    value={String(pageSize)}
                    onValueChange={(v) => {
                      setPageSize(Number(v));
                      setPage(1);
                    }}
                  >
                    <SelectTrigger className='h-8 w-[70px]'>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAGE_SIZE_OPTIONS.map((n) => (
                        <SelectItem key={n} value={String(n)}>
                          {n}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <span>
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant='outline'
                  size='sm'
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  <ChevronLeft className='h-4 w-4' />
                </Button>
                <Button
                  variant='outline'
                  size='sm'
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  <ChevronRight className='h-4 w-4' />
                </Button>
              </div>
            </div>
          </div>
        </>
      )}

      {editing && (
        <LocationDialog
          groupSlug={groupSlug}
          location={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            utils.internal.enrollmentSite.listLocations.invalidate();
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function LocationDialog({
  groupSlug,
  location,
  onClose,
  onSaved,
}: {
  groupSlug: string;
  location: LocationRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [label, setLabel] = useState(location?.label ?? '');
  const [accessCode, setAccessCode] = useState(location?.accessCode ?? '');
  const [agentId, setAgentId] = useState(location?.agentId ?? '');
  const [parentAgentId, setParentAgentId] = useState(location?.parentAgentId ?? '');
  const [city, setCity] = useState(location?.city ?? '');
  const [state, setState] = useState(location?.state ?? '');

  const create = trpc.internal.enrollmentSite.createLocation.useMutation({
    onSuccess: () => {
      toast({title: 'Access code added'});
      onSaved();
    },
    onError: createMutationErrorHandler('Could not add access code'),
  });
  const update = trpc.internal.enrollmentSite.updateLocation.useMutation({
    onSuccess: () => {
      toast({title: 'Access code updated'});
      onSaved();
    },
    onError: createMutationErrorHandler('Could not update access code'),
  });

  const pending = create.isPending || update.isPending;
  const canSubmit = !!label.trim() && !!accessCode.trim() && !!agentId.trim() && !pending;

  function submit() {
    const fields = {
      label: label.trim(),
      accessCode: accessCode.trim(),
      agentId: agentId.trim(),
      parentAgentId: parentAgentId.trim() || undefined,
      city: city.trim() || undefined,
      state: state.trim() || undefined,
    };
    if (location) update.mutate({id: location.id, ...fields});
    else create.mutate({groupSlug, ...fields});
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle>{location ? 'Edit access code' : 'Add access code'}</DialogTitle>
          <DialogDescription>
            The code members enter, its label, and the e123 agent its enrollments post under.
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-4'>
          <Field label='Label'>
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder='Cornerstone - Katy'
            />
          </Field>
          <div className='grid grid-cols-2 gap-3'>
            <Field label='Access code'>
              <Input
                value={accessCode}
                onChange={(e) => setAccessCode(e.target.value)}
                placeholder='10431'
              />
            </Field>
            <Field label='Agent ID (e123)'>
              <Input
                value={agentId}
                onChange={(e) => setAgentId(e.target.value)}
                placeholder='591697'
              />
            </Field>
          </div>
          <Field label='Parent agent ID' hint='Optional — the e123 parent agent, if any.'>
            <Input
              value={parentAgentId}
              onChange={(e) => setParentAgentId(e.target.value)}
              placeholder='944277'
            />
          </Field>
          <div className='grid grid-cols-2 gap-3'>
            <Field label='City'>
              <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder='Katy' />
            </Field>
            <Field label='State'>
              <Input value={state} onChange={(e) => setState(e.target.value)} placeholder='TX' />
            </Field>
          </div>
          <p className='text-xs text-muted-foreground'>
            When the site uses the location dropdown, members see this as “City, State” — or the
            label if these are blank.
          </p>
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSubmit}>
            {pending && <Loader2 className='mr-1.5 h-4 w-4 animate-spin' />}
            {location ? 'Save' : 'Add access code'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({label, hint, children}: {label: string; hint?: string; children: React.ReactNode}) {
  return (
    <div className='space-y-1.5'>
      <Label>{label}</Label>
      {children}
      {hint && <p className='text-xs text-muted-foreground'>{hint}</p>}
    </div>
  );
}
